import { useState } from 'react';
import type { EventType, SimulationResult } from '@openrocket-web/physics';
import { Chart } from './Chart';
import type { Fmt } from './format';
import { Icon } from './icons';
import { Tabs } from './Tabs';

export const EVENT_LABEL: Readonly<Record<EventType, string>> = {
  launch: 'Lanzamiento',
  ignition: 'Ignición',
  liftoff: 'Despegue',
  'launch-rod-clearance': 'Salida de la varilla',
  burnout: 'Fin de la combustión',
  'ejection-charge': 'Carga de eyección',
  apogee: 'Apogeo',
  'recovery-device-deployment': 'Despliegue del paracaídas',
  tumble: 'Volteo',
  'ground-hit': 'Impacto con el suelo',
  'simulation-end': 'Fin de la simulación',
};

export const STATUS_TEXT: Readonly<Record<SimulationResult['status'], string>> = {
  completed: 'Simulación completada',
  'no-liftoff': 'El cohete no despegó',
  'max-time': 'Se alcanzó el tiempo máximo',
  cancelled: 'Simulación cancelada',
  error: 'Error en la simulación',
};

type Tab = 'charts' | 'events' | 'summary';

const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'charts', label: 'Gráficas' },
  { id: 'events', label: 'Eventos' },
  { id: 'summary', label: 'Resumen' },
];

interface Props {
  /** Nombre base del archivo al exportar los datos. */
  readonly fileName: string;
  readonly result: SimulationResult;
  readonly fmt: Fmt;
  /** El diseño o las condiciones cambiaron después de calcular estos resultados. */
  readonly stale: boolean;
}

const COLUMNS: ReadonlyArray<[string, keyof SimulationResult['data']]> = [
  ['Tiempo (s)', 'time'],
  ['Altitud (m)', 'altitude'],
  ['Este (m)', 'east'],
  ['Norte (m)', 'north'],
  ['Velocidad vertical (m/s)', 'verticalVelocity'],
  ['Velocidad (m/s)', 'speed'],
  ['Aceleración (m/s²)', 'acceleration'],
  ['Mach', 'mach'],
  ['Empuje (N)', 'thrust'],
];

/** Los datos del vuelo como CSV (separador «;» y coma decimal evitada: se usa punto, en SI). */
export function flightCsv(result: SimulationResult): string {
  const rows = [COLUMNS.map(([h]) => h).join(';')];
  for (let i = 0; i < result.data.time.length; i++) rows.push(COLUMNS.map(([, k]) => String(Number(result.data[k][i]!.toPrecision(7)))).join(';'));
  return `${rows.join('\n')}\n`;
}

/** Descarga los datos del vuelo sin pasar por ningún servidor. */
export function downloadFlightCsv(fileName: string, result: SimulationResult): void {
  const url = URL.createObjectURL(new Blob(['\ufeff', flightCsv(result)], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName.replace(/[^\w.-]+/g, '_') || 'simulacion'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const max = (v: readonly number[]): number => v.reduce((m, x) => (Number.isFinite(x) && x > m ? x : m), -Infinity);

/** Contenido del diálogo de resultados: estado y avisos arriba, y pestañas de gráficas, eventos y resumen. */
export function Results({ fileName, result, fmt, stale }: Props): JSX.Element {
  const [tab, setTab] = useState<Tab>('charts');
  const { summary: s, data, events, warnings } = result;
  const ok = result.status === 'completed';
  const cards: Array<[string, string]> = [
    ['Apogeo', fmt.distance(s.apogee)],
    ['Velocidad máxima', fmt.speedText(s.maxVelocity)],
    ['Aceleración máxima', fmt.accelText(s.maxAcceleration)],
    ['Mach máximo', s.maxMach.toFixed(2)],
    ['Tiempo al apogeo', fmt.seconds(s.timeToApogee)],
    ['Tiempo de vuelo', fmt.seconds(s.flightTime)],
    ['Velocidad al salir de la varilla', Number.isFinite(s.rodClearanceVelocity) ? fmt.speedText(s.rodClearanceVelocity) : '—'],
    ['Velocidad de impacto', Number.isFinite(s.landingVelocity) ? fmt.speedText(s.landingVelocity) : '—'],
    ['Aterrizaje (este, norte)', Number.isFinite(s.landingVelocity) ? `${fmt.distance(s.landing.east, 3)}, ${fmt.distance(s.landing.north, 3)}` : '—'],
  ];

  const altitude = data.altitude.map((v) => fmt.dist.fromSI(v));
  const speed = data.speed.map((v) => fmt.speed.fromSI(v));
  const vertical = data.verticalVelocity.map((v) => fmt.speed.fromSI(v));
  const accel = data.acceleration.map((v) => fmt.accel.fromSI(v));

  return (
    <div className="results" data-testid="results">
      <p className={`status status--${ok ? 'ok' : 'warn'}`} role="status">
        {STATUS_TEXT[result.status]}
        {result.message !== undefined && `: ${result.message}`}
      </p>
      {stale && (
        <p className="notice notice--warning" role="status">
          Estos resultados corresponden a un diseño o unas condiciones anteriores. Vuelve a simular para actualizarlos.
        </p>
      )}
      {warnings.length > 0 && (
        <ul className="issue-list issue-list--warning" aria-label="Avisos de la simulación">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      <div className="toolbar">
        <button type="button" onClick={() => downloadFlightCsv(fileName, result)}>
          <Icon name="tool-plot" />
          Exportar datos (CSV)
        </button>
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} idPrefix="results" label="Secciones de los resultados" />
      <div role="tabpanel" id="results-panel" aria-labelledby={`results-tab-${tab}`} className="results__panel">
        {tab === 'summary' && (
          <dl className="cards">
            {cards.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        )}

        {tab === 'events' && (
          <table className="events">
            <caption className="sr-only">Eventos del vuelo en orden cronológico</caption>
            <thead>
              <tr>
                <th scope="col">Evento</th>
                <th scope="col">Tiempo</th>
                <th scope="col">Altitud</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={i}>
                  <th scope="row">{EVENT_LABEL[e.type]}</th>
                  <td>{fmt.seconds(e.time, 3)}</td>
                  <td>{fmt.distance(e.altitude, 4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'charts' && (
          <div className="charts">
            <Chart
              title="Altitud"
              xLabel="Tiempo (s)"
              yLabel={`Altitud (${fmt.dist.symbol})`}
              time={data.time}
              series={[{ label: 'Altitud', values: altitude, color: '#c2410c' }]}
              summary={`Máximo ${fmt.distance(s.apogee)} a los ${s.timeToApogee.toFixed(1)} s.`}
            />
            <Chart
              title="Velocidad"
              xLabel="Tiempo (s)"
              yLabel={`Velocidad (${fmt.speed.symbol})`}
              time={data.time}
              series={[
                { label: 'Total', values: speed, color: '#0f766e' },
                { label: 'Vertical', values: vertical, color: '#7c3aed' },
              ]}
              summary={`Máxima ${fmt.speedText(s.maxVelocity)}.`}
            />
            <Chart
              title="Aceleración"
              xLabel="Tiempo (s)"
              yLabel={`Aceleración (${fmt.accel.symbol})`}
              time={data.time}
              series={[{ label: 'Aceleración', values: accel, color: '#b91c1c' }]}
              summary={`Máxima ${fmt.accelText(Number.isFinite(max(data.acceleration)) ? max(data.acceleration) : 0)}.`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
